import { atom } from "jotai";
import {
  AGENT_CHAT_OVERRIDE_KEY,
  API_SERVER_OVERRIDE_KEY,
} from "~/config/configProvider";

/**
 * Dev-only override for the API server base URL. When set, configProvider's
 * getConfig() returns this value as API_SERVER_BASE_URL on dev builds. Read
 * by the bottom-center SandboxOverrideBanner and the debug-page section.
 *
 * Stored in localStorage as a raw string (NOT JSON) so configProvider can
 * read it directly without parsing. Stage/prod builds ignore the key —
 * see configProvider.ts:getConfig.
 */
function readInitialOverride(): string | null {
  try {
    return localStorage.getItem(API_SERVER_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

const baseUrlAtom = atom<string | null>(readInitialOverride());

export const sandboxOverrideUrlAtom = atom(
  (get) => get(baseUrlAtom),
  (_get, set, newUrl: string | null) => {
    set(baseUrlAtom, newUrl);
    try {
      if (newUrl) {
        localStorage.setItem(API_SERVER_OVERRIDE_KEY, newUrl);
      } else {
        localStorage.removeItem(API_SERVER_OVERRIDE_KEY);
      }
    } catch {
      // localStorage unavailable (e.g. SSR / strict private mode) — atom
      // state still updates so the UI stays consistent within this tab.
    }
  },
);

/**
 * Dev-only override for Indy's AgentChat Function URL (a per-dev Lambda
 * sandbox). Same raw-string localStorage contract as the API server override;
 * configProvider reads AGENT_CHAT_OVERRIDE_KEY directly.
 */
function readInitialAgentChatOverride(): string | null {
  try {
    return localStorage.getItem(AGENT_CHAT_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

const agentChatUrlAtom = atom<string | null>(readInitialAgentChatOverride());

export const agentChatOverrideUrlAtom = atom(
  (get) => get(agentChatUrlAtom),
  (_get, set, newUrl: string | null) => {
    set(agentChatUrlAtom, newUrl);
    try {
      if (newUrl) {
        localStorage.setItem(AGENT_CHAT_OVERRIDE_KEY, newUrl);
      } else {
        localStorage.removeItem(AGENT_CHAT_OVERRIDE_KEY);
      }
    } catch {
      // localStorage unavailable — atom state still updates for this tab.
    }
  },
);

export type SandboxHealthStatus = "up" | "down" | "unknown";

export interface SandboxHealthState {
  status: SandboxHealthStatus;
  lastCheckedAt: number | null;
}

export const sandboxHealthAtom = atom<SandboxHealthState>({
  status: "unknown",
  lastCheckedAt: null,
});
